import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  ArchiveRestore,
  MoreHorizontal,
  Pencil,
  Plus,
} from "lucide-react";
import {
  categoriesApi,
  type CategoryDto,
  type CreateCategoryPayload,
  type UpdateCategoryPayload,
} from "@/api/lookups";
import { ApiError } from "@/api/client";
import {
  Badge,
  Button,
  FormField,
  Input,
} from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { Textarea } from "@/components/ui/Select";
import { useToast } from "@/components/ui/Toast";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/design-system/lib/utils";
import { useDateFormatter } from "@/utils/datetime";

/**
 * Admin-managed Lookups v1.0 §4b — admin CRUD for the Category
 * taxonomy table seeded by the AddAdminManagedLookups migration. Same
 * UX as <see cref="AdminRolesPage"/> minus the system-reserved guard
 * (categories have no equivalent of ADMIN — every row is archivable).
 *
 * Mutations invalidate <c>['admin-categories']</c> (this page) and
 * <c>['categories']</c> (the user-facing lookup that drives the
 * Report Template editor's category dropdown and the badge rendering
 * on the Report Templates list view) so display-name changes
 * propagate immediately across the app.
 */
export function AdminCategoriesPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("Lookups.ManageCategories");
  const qc = useQueryClient();
  const { toast } = useToast();
  const { date } = useDateFormatter();
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [editTarget, setEditTarget] = useState<CategoryDto | "new" | null>(null);

  const list = useQuery({
    queryKey: ["admin-categories", true],
    queryFn: () => categoriesApi.adminList(true),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["admin-categories"] });
    void qc.invalidateQueries({ queryKey: ["categories"] });
  };

  const archiveMutation = useMutation({
    mutationFn: (cat: CategoryDto) =>
      cat.isActive
        ? categoriesApi.adminArchive(cat.code)
        : categoriesApi.adminUnarchive(cat.code),
    onSuccess: (_data, cat) => {
      toast({
        tone: "success",
        message: cat.isActive
          ? `Archived ${cat.displayName}. Toggle 'Show archived' to find it again.`
          : `Restored ${cat.displayName}.`,
      });
      invalidate();
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  const filtered = (list.data ?? [])
    .filter((c) => (showArchived ? true : c.isActive))
    .filter((c) => {
      if (!search) return true;
      const needle = search.toLowerCase();
      return (
        c.code.toLowerCase().includes(needle) ||
        c.displayName.toLowerCase().includes(needle)
      );
    });
  const archivedCount = (list.data ?? []).filter((c) => !c.isActive).length;

  const columns: Column<CategoryDto>[] = [
    {
      key: "code",
      header: "Code",
      render: (c) => (
        <span
          className={cn(
            "font-mono text-onyx font-semibold",
            !c.isActive && "opacity-50",
          )}
        >
          {c.code}
        </span>
      ),
    },
    {
      key: "displayName",
      header: "Display name",
      render: (c) => (
        <div className={cn(!c.isActive && "opacity-50")}>
          <div className="font-semibold text-onyx flex items-center gap-space-2">
            <span>{c.displayName}</span>
            {!c.isActive && <Badge status="archived">Archived</Badge>}
          </div>
          {c.description && (
            <div className="text-caption text-gray-mid font-normal max-w-md truncate">
              {c.description}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "displayOrder",
      header: "Order",
      render: (c) => (
        <span className={cn("font-mono text-caption", !c.isActive && "opacity-50")}>
          {c.displayOrder}
        </span>
      ),
    },
    {
      key: "modified",
      header: "Last modified",
      render: (c) => (
        <span className={cn("text-caption text-gray-mid", !c.isActive && "opacity-50")}>
          {date(c.modifiedAt)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (c) =>
        canManage ? (
          <RowActions
            category={c}
            archivePending={archiveMutation.isPending}
            onEdit={() => setEditTarget(c)}
            onArchiveToggle={() => archiveMutation.mutate(c)}
          />
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Admin"
        title="Categories"
        description="The platform's report category taxonomy — codes, display names, and ordering. Archived categories disappear from new-template dropdowns but existing templates referencing them survive. Codes are permanent once issued; rename the display name instead."
        actions={
          canManage && (
            <Button onClick={() => setEditTarget("new")}>
              <Plus size={16} aria-hidden /> New category
            </Button>
          )
        }
      />

      <div className="flex items-center gap-space-3 mb-space-3 flex-wrap">
        <Input
          type="search"
          placeholder="Search code or display name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
        <button
          type="button"
          onClick={() => setShowArchived((v) => !v)}
          aria-pressed={showArchived}
          className={cn(
            "px-space-2 py-[6px] rounded-full text-caption font-display font-semibold border transition-colors",
            showArchived
              ? "bg-gold/15 border-gold text-gold-shadow"
              : "bg-white-pure border-border-light text-gray-mid hover:border-gold-shadow",
          )}
        >
          Show archived{archivedCount > 0 ? ` (${archivedCount})` : ""}
        </button>
      </div>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={list.isLoading}
        error={list.isError ? (list.error as Error).message : null}
        emptyMessage={
          canManage
            ? "No categories match your filter. Click 'New category' to add one."
            : "No categories available."
        }
        rowKey={(c) => c.code}
      />

      {editTarget !== null && (
        <CategoryEditModal
          target={editTarget}
          existingCodes={list.data?.map((c) => c.code) ?? []}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            invalidate();
          }}
        />
      )}
    </>
  );
}

function RowActions({
  category,
  archivePending,
  onEdit,
  onArchiveToggle,
}: {
  category: CategoryDto;
  archivePending: boolean;
  onEdit: () => void;
  onArchiveToggle: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="flex items-center gap-space-1 justify-end" ref={ref}>
      <button
        type="button"
        onClick={onEdit}
        className="p-space-1 rounded-md text-gold-shadow hover:bg-white-warm"
        aria-label={`Edit ${category.code}`}
      >
        <Pencil size={16} aria-hidden />
      </button>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="p-space-1 rounded-md text-gray-mid hover:bg-white-warm hover:text-onyx"
          aria-label={`More actions for ${category.code}`}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <MoreHorizontal size={16} aria-hidden />
        </button>
        {open && (
          <div
            role="menu"
            className="absolute right-0 mt-1 w-56 z-20 bg-white-pure border border-border-light rounded-md shadow-lg overflow-hidden"
          >
            <button
              type="button"
              role="menuitem"
              disabled={archivePending}
              onClick={() => {
                setOpen(false);
                onArchiveToggle();
              }}
              className="w-full flex items-center gap-space-2 px-space-3 py-space-2 text-small text-onyx hover:bg-white-warm disabled:opacity-50"
            >
              {category.isActive ? (
                <>
                  <Archive size={14} aria-hidden /> Archive
                </>
              ) : (
                <>
                  <ArchiveRestore size={14} aria-hidden /> Unarchive
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface CategoryEditModalProps {
  target: CategoryDto | "new";
  existingCodes: string[];
  onClose: () => void;
  onSaved: () => void;
}

const CATEGORY_CODE_REGEX = /^[A-Za-z0-9]{1,50}$/;

function CategoryEditModal({ target, existingCodes, onClose, onSaved }: CategoryEditModalProps) {
  const isNew = target === "new";
  const initial: CategoryDto | undefined = isNew ? undefined : target;
  const { toast } = useToast();
  const [code, setCode] = useState(initial?.code ?? "");
  const [displayName, setDisplayName] = useState(initial?.displayName ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [displayOrder, setDisplayOrder] = useState<string>(
    initial?.displayOrder !== undefined ? String(initial.displayOrder) : "0",
  );
  const [errors, setErrors] = useState<string[]>([]);

  const createMutation = useMutation({
    mutationFn: (body: CreateCategoryPayload) => categoriesApi.adminCreate(body),
    onSuccess: (dto) => {
      toast({ tone: "success", message: `Created ${dto.displayName}.` });
      onSaved();
    },
    onError: (e: Error) => surfaceError(e),
  });
  const updateMutation = useMutation({
    mutationFn: ({ code, body }: { code: string; body: UpdateCategoryPayload }) =>
      categoriesApi.adminUpdate(code, body),
    onSuccess: (dto) => {
      toast({ tone: "success", message: `Saved ${dto.displayName}.` });
      onSaved();
    },
    onError: (e: Error) => surfaceError(e),
  });

  const surfaceError = (e: Error) => {
    if (e instanceof ApiError && e.errors.length) {
      setErrors(e.errors);
    } else {
      setErrors([e.message]);
    }
  };

  const validate = (): string[] => {
    const issues: string[] = [];
    if (isNew) {
      const trimmed = code.trim();
      if (!trimmed) issues.push("Code is required.");
      else if (!CATEGORY_CODE_REGEX.test(trimmed))
        issues.push("Code must be 1–50 letters or digits (no hyphens or spaces).");
      else if (
        existingCodes.some((c) => c.toLowerCase() === trimmed.toLowerCase())
      )
        issues.push(`Code '${trimmed}' already exists.`);
    }
    if (!displayName.trim()) issues.push("Display name is required.");
    if (displayName.length > 100)
      issues.push("Display name must be 100 characters or fewer.");
    if (description.length > 500)
      issues.push("Description must be 500 characters or fewer.");
    return issues;
  };

  const onSubmit = () => {
    setErrors([]);
    const issues = validate();
    if (issues.length) {
      setErrors(issues);
      return;
    }
    const order = parseInt(displayOrder, 10);
    const body = {
      displayName: displayName.trim(),
      description: description.trim() || null,
      displayOrder: Number.isFinite(order) ? order : 0,
    };
    if (isNew) {
      createMutation.mutate({ ...body, code: code.trim() });
    } else if (initial) {
      updateMutation.mutate({ code: initial.code, body });
    }
  };

  const pending = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={isNew ? "New category" : `Edit ${initial?.code}`}
      description={
        isNew
          ? "Add a new report category. The code is permanent once saved — pick something stable like 'WeeklyReports'."
          : "Update the display name, description, or sort order. Codes are permanent because existing report templates reference them."
      }
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={onSubmit} loading={pending}>
            {isNew ? "Create category" : "Save changes"}
          </Button>
        </>
      }
    >
      <div className="space-y-space-3">
        <FormField
          label="Code"
          required={isNew}
          htmlFor="cat-code"
          helperText={
            isNew
              ? "Letters and digits only (no hyphens or spaces). Max 50 characters. Examples: LoanLevel, WeeklyDealReports."
              : "Codes are permanent — existing report templates reference this code. Edit the display name instead if you need to rename the category."
          }
        >
          {isNew ? (
            <Input
              id="cat-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g. WeeklyReports"
              maxLength={50}
              autoFocus
              className="font-mono"
            />
          ) : (
            <div
              id="cat-code"
              className="font-mono text-onyx font-semibold px-space-2 py-[6px] bg-white-warm border border-border-light rounded-md inline-block"
              title="Codes are permanent — existing report templates reference this code."
            >
              {initial?.code}
            </div>
          )}
        </FormField>

        <FormField label="Display name" required htmlFor="cat-name">
          <Input
            id="cat-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Weekly Reports"
            maxLength={100}
            autoFocus={!isNew}
          />
        </FormField>

        <FormField
          label="Description"
          htmlFor="cat-desc"
          helperText="Optional internal note. Not shown to non-admin users."
        >
          <Textarea
            id="cat-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            maxLength={500}
          />
        </FormField>

        <FormField
          label="Display order"
          htmlFor="cat-order"
          helperText="Lower numbers appear first. Increment by 10 to leave room for future inserts."
        >
          <Input
            id="cat-order"
            type="number"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(e.target.value)}
            className="max-w-[8rem] font-mono"
          />
        </FormField>

        {errors.length > 0 && (
          <ul
            className="list-disc pl-space-4 text-caption text-status-error"
            role="alert"
          >
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
